import React, { useContext, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { LineHeightIcon } from '@radix-ui/react-icons';
import { IconButton } from '@producer/ui-kit';

import { CommentCheckbox } from './CommentCheckbox';
import { CommentsList } from './CommentsList';
import { AddComment } from './AddComment';
import { api } from '../../api';
import { BufferToBlob } from '../../lib/utils/BufferToBolb';
import { isVideo } from '../../core/services/file.service';
import { DropdownMenu } from '../modals/dropdown-menu/DropdownMenu';
import { useResponsive } from '../../hooks/useResponsive';
import {
  CommentContext,
  CommentSortOrder,
} from '../../core/contexts/comment-context/comment.context';
import { SingleSelectDropdown } from '../select-dropdown';

import type { Asset, Comment } from '../../app/entities';

import styles from './Comments.module.scss';
const commentSortOptions = [
  { value: 'timestamp', label: 'Timecode' },
  { value: 'createdAt', label: 'Oldest first' },
  { value: '-createdAt', label: 'Newest first' },
];

interface CommentsProps {
  timeTagRef: React.RefObject<HTMLSpanElement>;
  asset: Asset;
  showHeader?: boolean;
  defaultShowCompleted?: boolean;
}

export const Comments: React.FC<CommentsProps> = observer(
  ({ asset, timeTagRef, showHeader = true, defaultShowCompleted }) => {
    const { t } = useTranslation('project');
    const { isDesktop } = useResponsive();

    const [showCompleted, setShowCompleted] = useState(!!defaultShowCompleted);
    const { playerRef, pdfCurrentPage, sortOrder, setSortOrder } = useContext(CommentContext);

    const mimeType = asset.fileType ? asset.fileType.split('/')[0] : 'unknown';

    const handleShowCompleted = () => {
      setShowCompleted(!showCompleted);
    };

    const handleSortOrderChange = (value?: string) => {
      // Unchecking the active option keeps the current order — there is no "unsorted" state
      if (!value) {
        return;
      }

      setSortOrder(value as CommentSortOrder);
    };

    const handleDownloadResolve = async () => {
      const { data } = await api.get(`/assets/${asset._id}/comments`, {
        headers: {
          Accept: 'text/edl',
        },
      });

      BufferToBlob(data, `${asset.name}.edl`);
    };

    const handleAddComment = async (
      comment: Pick<Comment, 'text' | 'timestamp' | 'page' | 'annotations'>,
    ): Promise<string> => {
      const commentId: string = await asset.addComment(comment);

      return commentId;
    };

    return (
      <div className={styles['container']}>
        {showHeader && (
          <>
            <div className={styles['header']}>
              <h6 className={styles['title']}>{asset.commentCount} comments</h6>

              {isDesktop && !!asset.fileType && isVideo(asset.fileType) && (
                <DropdownMenu
                  key="download-file"
                  items={[
                    {
                      title: 'Download for Resolve',
                      onSelect: handleDownloadResolve,
                      icon: null,
                    },
                  ]}
                  size={'small'}
                />
              )}

              {!isDesktop && (
                <label className={styles['is-completed-label']}>
                  {t('common:show_completed')}

                  <CommentCheckbox checked={showCompleted} onClick={handleShowCompleted} />
                </label>
              )}
            </div>

            {isDesktop && (
              <div className={styles['header']}>
                <SingleSelectDropdown
                  title="Sort by"
                  value={sortOrder}
                  onValueChange={handleSortOrderChange}
                  options={commentSortOptions}
                  align="start"
                  trigger={() => (
                    <IconButton
                      icon={<LineHeightIcon />}
                      variant="transparent"
                      aria-label="Sort comments"
                    />
                  )}
                />

                <label className={styles['is-completed-label']}>
                  {t('common:show_completed')}

                  <CommentCheckbox checked={showCompleted} onClick={handleShowCompleted} />
                </label>
              </div>
            )}
          </>
        )}

        <CommentsList showCompleted={showCompleted} />

        <div className={styles['footer']}>
          <AddComment
            onAddComment={handleAddComment}
            mimeType={mimeType}
            playerRef={playerRef}
            pdfCurrentPage={pdfCurrentPage}
            timeTagRef={timeTagRef}
          />
        </div>
      </div>
    );
  },
);
